import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Link, Text, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {useAnalytics, useHeaderSize, useHeaderSizeWithoutBanner} from '_customHooks'
import type {PageContentStoriesLayoutComponent} from '_utils'

export interface StoriesNavProps {
    /** Page title, taken from the Page Designer page `name` attribute. */
    title?: string
    /** Optional intro paragraph authored on the `pwaStoriesMainLayout` wrapper. */
    description?: string
    sections: PageContentStoriesLayoutComponent[]
    basePath: string
    /** Slug of the section currently expanded, or `undefined` on the overview. */
    activeSectionId?: string
    /** The expanded section: below `lg` its title and subtitle replace the page ones. */
    activeSection?: PageContentStoriesLayoutComponent
}

/**
 * @name StoriesNav
 *
 * @component
 *
 * @description
 * Renders the Stories sidebar: page title, the section navigation and the optional intro text.
 * The navigation entries are derived from the titles of the sections included in the page and
 * are always preceded by a "View all" entry that links back to the Stories overview.
 * Below `lg` the sidebar collapses into an accordion whose trigger is the current entry, and the
 * heading shows the expanded section title instead of the page one.
 *
 * @param {StoriesNavProps} props - The properties object.
 * @param {string} [props.title] - The page title.
 * @param {string} [props.description] - The intro paragraph.
 * @param {PageContentStoriesLayoutComponent[]} props.sections - The Stories sections.
 * @param {string} props.basePath - Base path of the Stories page.
 * @param {string} [props.activeSectionId] - The currently expanded section.
 * @param {PageContentStoriesLayoutComponent} [props.activeSection] - The currently expanded section.
 *
 * @returns {JSX.Element} The rendered `StoriesNav` component.
 */
const StoriesNav: React.FC<StoriesNavProps> = ({title, description, sections, basePath, activeSectionId, activeSection}) => {
    const {formatMessage} = useIntl()
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
    const headerSize = useHeaderSizeWithoutBanner()
    const {headerSize: headerSizeWithBanner} = useHeaderSize()
    const styles = useMultiStyleConfig('Stories', {headerSize, headerSizeWithBanner, activeSection: !!activeSection})
    const {analyticsEventStoriesMenuClick} = useAnalytics()

    const viewAllLabel = formatMessage({id: 'stories.nav.view_all', defaultMessage: 'View all'})
    // `title` is optional (nested sections may not need one); `name` is always authored, so it drives the nav.
    const navSections = sections.filter((section) => section.sectionId)

    const entries = [
        {key: 'stories-view-all', label: viewAllLabel, name: 'view_all', to: basePath, sectionId: undefined as string | undefined},
        ...navSections.map((section) => ({
            key: section.id ?? (section.sectionId as string),
            label: section.title || section.name || '',
            to: `${basePath}/${section.sectionId}`,
            sectionId: section.sectionId,
        })),
    ]
    const selectedEntry = entries.find((entry) => entry.sectionId === activeSectionId) ?? entries[0]

    const handleLinkClick = (entry: (typeof entries)[number]) => {
        analyticsEventStoriesMenuClick({action: entry.label})
    }

    const renderLink = (entry: (typeof entries)[number], isActive: boolean) => (
        <Link
            key={entry.key}
            as={RouteLink}
            to={entry.to}
            sx={styles.navLink}
            aria-current={isActive ? 'page' : undefined}
            data-active={isActive}
            onClick={() => handleLinkClick(entry)}
        >
            {entry.label}
        </Link>
    )

    return (
        <Box as="aside" sx={styles.sidebar} data-testid="stories-nav">
            <VStack display={{base: 'none', lg: 'flex'}} align="stretch" spacing="spacing.6" data-testid="stories-nav-desktop">
                {title && (
                    <Text as="h1" sx={styles.pageTitle}>
                        {title}
                    </Text>
                )}

                {description && <Text dangerouslySetInnerHTML={{__html: description}} sx={styles.sidebarDescription}></Text>}

                <VStack as="nav" sx={styles.nav} align="stretch">
                    {entries.map((entry) => renderLink(entry, entry.sectionId === activeSectionId))}
                </VStack>
            </VStack>

            <VStack display={{base: 'flex', lg: 'none'}} align="stretch" spacing="spacing.4" data-testid="stories-nav-mobile">
                <Text as="h1" sx={styles.mobileTitle}>
                    {title}
                </Text>
                {description && <Text dangerouslySetInnerHTML={{__html: description}} sx={styles.mobileDescription}></Text>}

                <Accordion allowToggle index={isMobileNavOpen ? 0 : -1} onChange={(index) => setIsMobileNavOpen(index === 0)} sx={styles.accordion}>
                    <AccordionItem sx={styles.accordionItem}>
                        {!isMobileNavOpen && (
                            <AccordionButton sx={styles.accordionButton}>
                                <Box as="span" flex="1" textAlign="left">
                                    {selectedEntry.label}
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                        )}
                        <AccordionPanel sx={styles.accordionPanel}>
                            {isMobileNavOpen && (
                                <VStack as="nav" sx={styles.nav} align="stretch">
                                    {entries.map((entry) =>
                                        entry.key === selectedEntry.key ? (
                                            <AccordionButton
                                                key={entry.key}
                                                sx={styles.accordionButton}
                                                aria-current={entry.sectionId === activeSectionId ? 'page' : undefined}
                                            >
                                                <Box as="span" flex="1" textAlign="left">
                                                    {entry.label}
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                        ) : (
                                            renderLink(entry, false)
                                        ),
                                    )}
                                </VStack>
                            )}
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </VStack>
        </Box>
    )
}

export default StoriesNav
